import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const STAR_DENSITY = 0.0001;
const STARFIELD_HEIGHT = 0.7;
const LABEL_POOL = [
	['COMING SOON', 'PROXIMAMENTE', 'PROCHAINEMENT', 'PROSSIMAMENTE', 'BALD IM KINO'],
	['NOW SHOWING', 'EN CARTELERA', 'A L AFFICHE', 'ORA IN PROGRAMMA', 'JETZT IM KINO'],
	['IN THEATERS', 'EN CINES', 'AU CINEMA', 'AL CINEMA', 'IM KINO'],
	['NOW PLAYING', 'YA EN CARTELERA', 'EN SALLE', 'ORA AL CINEMA', 'JETZT LAUFT'],
	['EXPERIENCE IT', 'VIVELO', 'VIVEZ LE', 'VIVILO', 'ERLEBE ES'],
	['ONLY IN CINEMAS', 'SOLO EN CINES', 'UNIQUEMENT AU CINEMA', 'SOLO AL CINEMA', 'NUR IM KINO'],
	['THIS SUMMER', 'ESTE VERANO', 'CET ETE', 'QUEST ESTATE', 'DIESEN SOMMER'],
	['THIS FALL', 'ESTE OTONO', 'CET AUTOMNE', 'QUESTO AUTUNNO', 'DIESEN HERBST']
];
const SPOTLIGHTS = [
	{
		x: 0.18, y: 1.02, angle: -0.95, swing: 0.28, speed: 0.00042, width: 0.1, alpha: 0.05
	},
	{
		x: 0.42, y: 1.04, angle: -1.42, swing: 0.2, speed: 0.00035, width: 0.12, alpha: 0.04
	},
	{
		x: 0.66, y: 1.03, angle: -1.78, swing: 0.24, speed: 0.00031, width: 0.11, alpha: 0.045
	},
	{
		x: 0.86, y: 1.01, angle: -2.16, swing: 0.22, speed: 0.00038, width: 0.09, alpha: 0.035
	}
];

function clamp(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function hslVar(styles, variableName, alpha = 1, fallback = `hsla(0, 0%, 0%, ${alpha})`) {
	const value = styles.getPropertyValue(variableName).trim();
	if(!value) {
		return fallback;
	}

	const [h, s, l] = value.split(/\s+/);
	if(!h || !s || !l) {
		return fallback;
	}

	return `hsla(${h}, ${s}, ${l}, ${alpha})`;
}

function getThemePalette() {
	const styles = window.getComputedStyle(document.documentElement);

	return {
		bgTop: hslVar(styles, '--midnight-900', 1, 'hsla(220, 50%, 4%, 1)'),
		bgMid: hslVar(styles, '--midnight-base', 1, 'hsla(220, 45%, 7%, 1)'),
		bgBottom: hslVar(styles, '--midnight-100', 1, 'hsla(220, 40%, 10%, 1)'),
		overlay: hslVar(styles, '--black', 0.22, 'hsla(0, 0%, 0%, 0.22)'),
		label: hslVar(styles, '--silver-100', 1, 'hsla(220, 40%, 85%, 1)'),
		star: hslVar(styles, '--white', 1, 'hsla(0, 0%, 100%, 1)'),
		spotlightWarm: hslVar(styles, '--yellow-base', 1, 'hsla(45, 95%, 70%, 1)'),
		spotlightCool: hslVar(styles, '--primary', 1, 'hsla(210, 90%, 65%, 1)')
	};
}

function createStars(width, height, pixelRatio) {
	const count = Math.max(45, Math.floor(width * height * STAR_DENSITY));
	const starfieldHeight = height * STARFIELD_HEIGHT;

	return Array.from({ length: count }, () => ({
		x: Math.random() * width,
		y: Math.random() * starfieldHeight,
		radius: (0.5 + Math.random() * 1.2) * pixelRatio,
		alpha: 0.04 + Math.random() * 0.06,
		twinkle: 0.0012 + Math.random() * 0.003,
		phase: Math.random() * Math.PI * 2
	}));
}

function randomLabel(width, height) {
	const textSet = LABEL_POOL[Math.floor(Math.random() * LABEL_POOL.length)];
	const text = textSet[Math.floor(Math.random() * textSet.length)];
	const fadeIn = 90 + Math.random() * 60;
	const hold = 120 + Math.random() * 180;

	return {
		text,
		x: 40 + Math.random() * Math.max(40, width - 80),
		y: 30 + Math.random() * Math.max(40, height - 120),
		fontSize: 10 + Math.floor(Math.random() * 8),
		maxAlpha: 0.04 + Math.random() * 0.05,
		alpha: 0,
		fadeIn,
		hold,
		holdLeft: hold,
		phase: 'in'
	};
}

function createLabels(width, height) {
	return Array.from({ length: 8 }, () => {
		const label = randomLabel(width, height);
		label.alpha = Math.random() * label.maxAlpha;
		label.phase = 'hold';
		label.holdLeft = Math.random() * label.hold;
		return label;
	});
}

function updateLabels(labels, width, height) {
	for(let i = 0; i < labels.length; i += 1) {
		const label = labels[i];

		if(label.phase === 'in') {
			label.alpha += label.maxAlpha / label.fadeIn;
			if(label.alpha >= label.maxAlpha) {
				label.alpha = label.maxAlpha;
				label.phase = 'hold';
			}
		}
		else if(label.phase === 'hold') {
			label.holdLeft -= 1;
			if(label.holdLeft <= 0) {
				label.phase = 'out';
			}
		}
		else {
			label.alpha -= label.maxAlpha / label.fadeIn;
			if(label.alpha <= 0) {
				labels[i] = randomLabel(width, height);
			}
		}
	}
}

function drawLabels(ctx, labels, palette) {
	labels.forEach((label) => {
		ctx.save();
		ctx.globalAlpha = Math.max(0, label.alpha);
		ctx.fillStyle = palette.label;
		ctx.font = `500 ${label.fontSize}px Inter, Arial, sans-serif`;
		ctx.textAlign = 'center';
		ctx.fillText(label.text, label.x, label.y);
		ctx.restore();
	});
}

function drawBackground(ctx, width, height, palette) {
	const gradient = ctx.createLinearGradient(0, 0, 0, height);
	gradient.addColorStop(0, palette.bgTop);
	gradient.addColorStop(0.55, palette.bgMid);
	gradient.addColorStop(1, palette.bgBottom);

	ctx.fillStyle = gradient;
	ctx.fillRect(0, 0, width, height);

	ctx.fillStyle = palette.overlay;
	ctx.fillRect(0, 0, width, height);
}

function drawStars(ctx, stars, time, palette) {
	stars.forEach((star) => {
		const pulse = 0.55 + ((Math.sin(time * star.twinkle + star.phase) + 1) * 0.5) * 0.9;
		ctx.beginPath();
		ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
		ctx.fillStyle = hslVar(window.getComputedStyle(document.documentElement), '--white', clamp(star.alpha * pulse, 0, 1), palette.star);
		ctx.fill();
	});
}

function drawShootingStar(ctx, width, height, time, palette) {
	const cycle = 9000;
	const progress = (time % cycle) / cycle;

	if(progress > 0.16) {
		return;
	}

	const t = progress / 0.16;
	const startX = width * 0.72;
	const startY = height * 0.18;
	const endX = width * 0.28;
	const endY = height * 0.42;
	const x = startX + (endX - startX) * t;
	const y = startY + (endY - startY) * t;
	const tailLength = width * 0.14;
	const angle = Math.atan2(endY - startY, endX - startX);
	const tx = Math.cos(angle) * tailLength;
	const ty = Math.sin(angle) * tailLength;
	const alpha = Math.sin(t * Math.PI) * 0.45;

	const grad = ctx.createLinearGradient(x, y, x - tx, y - ty);
	grad.addColorStop(0, hslVar(window.getComputedStyle(document.documentElement), '--white', alpha, palette.star));
	grad.addColorStop(0.4, hslVar(window.getComputedStyle(document.documentElement), '--primary', alpha * 0.5, palette.spotlightCool));
	grad.addColorStop(1, hslVar(window.getComputedStyle(document.documentElement), '--primary', 0, palette.spotlightCool));

	ctx.beginPath();
	ctx.moveTo(x, y);
	ctx.lineTo(x - tx, y - ty);
	ctx.strokeStyle = grad;
	ctx.lineWidth = 2;
	ctx.lineCap = 'round';
	ctx.stroke();

	ctx.beginPath();
	ctx.arc(x, y, 1.8, 0, Math.PI * 2);
	ctx.fillStyle = hslVar(window.getComputedStyle(document.documentElement), '--white', alpha, palette.star);
	ctx.fill();
}

function drawSpotlight(ctx, width, height, spotlight, time, palette) {
	const baseX = spotlight.x * width;
	const baseY = spotlight.y * height;
	const angle = spotlight.angle + Math.sin(time * spotlight.speed) * spotlight.swing;
	const beamLength = height * 1.45;
	const farX = baseX + Math.cos(angle) * beamLength;
	const farY = baseY + Math.sin(angle) * beamLength;
	const halfWidth = width * spotlight.width;
	const perpX = -Math.sin(angle);
	const perpY = Math.cos(angle);
	const apexWidth = width * 0.012;
	const gradient = ctx.createLinearGradient(baseX, baseY, farX, farY);

	gradient.addColorStop(0, hslVar(window.getComputedStyle(document.documentElement), '--yellow-base', spotlight.alpha * 0.08, palette.spotlightWarm));
	gradient.addColorStop(0.15, hslVar(window.getComputedStyle(document.documentElement), '--yellow-base', spotlight.alpha, palette.spotlightWarm));
	gradient.addColorStop(0.82, hslVar(window.getComputedStyle(document.documentElement), '--primary', spotlight.alpha * 0.08, palette.spotlightCool));
	gradient.addColorStop(1, hslVar(window.getComputedStyle(document.documentElement), '--primary', 0, palette.spotlightCool));

	ctx.beginPath();
	ctx.moveTo(baseX - perpX * apexWidth, baseY - perpY * apexWidth);
	ctx.lineTo(baseX + perpX * apexWidth, baseY + perpY * apexWidth);
	ctx.lineTo(farX + perpX * halfWidth, farY + perpY * halfWidth);
	ctx.lineTo(farX - perpX * halfWidth, farY - perpY * halfWidth);
	ctx.closePath();
	ctx.fillStyle = gradient;
	ctx.fill();

	const glow = ctx.createRadialGradient(baseX, baseY, 0, baseX, baseY, width * 0.08);
	glow.addColorStop(0, hslVar(window.getComputedStyle(document.documentElement), '--yellow-base', spotlight.alpha * 0.16, palette.spotlightWarm));
	glow.addColorStop(1, hslVar(window.getComputedStyle(document.documentElement), '--yellow-base', 0, palette.spotlightWarm));
	ctx.fillStyle = glow;
	ctx.beginPath();
	ctx.arc(baseX, baseY, width * 0.08, 0, Math.PI * 2);
	ctx.fill();
}

export default function SpotlightBackground({ themeKey }) {
	const canvasRef = useRef(null);
	const animationFrameRef = useRef(0);
	const starsRef = useRef([]);
	const labelsRef = useRef([]);
	const [isTransitioning, setIsTransitioning] = useState(false);

	useEffect(() => {
		setIsTransitioning(true);
		const timeout = window.setTimeout(() => setIsTransitioning(false), 650);
		return () => window.clearTimeout(timeout);
	}, [themeKey]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if(!canvas) {
			return undefined;
		}

		const context = canvas.getContext('2d');
		if(!context) {
			return undefined;
		}

		const resize = () => {
			const pixelRatio = window.devicePixelRatio || 1;
			const width = window.innerWidth;
			const height = window.innerHeight;

			canvas.width = Math.floor(width * pixelRatio);
			canvas.height = Math.floor(height * pixelRatio);
			canvas.style.width = '100%';
			canvas.style.height = '100%';

			context.setTransform(1, 0, 0, 1, 0, 0);
			context.scale(pixelRatio, pixelRatio);
			starsRef.current = createStars(width, height, pixelRatio);
			labelsRef.current = createLabels(width, height);
		};

		const render = (time) => {
			const width = canvas.clientWidth;
			const height = canvas.clientHeight;
			const palette = getThemePalette();

			context.clearRect(0, 0, width, height);
			drawBackground(context, width, height, palette);

			context.save();
			context.globalCompositeOperation = 'screen';
			drawStars(context, starsRef.current, time, palette);
			drawShootingStar(context, width, height, time, palette);
			updateLabels(labelsRef.current, width, height);
			drawLabels(context, labelsRef.current, palette);
			SPOTLIGHTS.forEach((spotlight) => {
				drawSpotlight(context, width, height, spotlight, time, palette);
			});
			context.restore();

			animationFrameRef.current = window.requestAnimationFrame(render);
		};

		resize();
		window.addEventListener('resize', resize);
		animationFrameRef.current = window.requestAnimationFrame(render);

		return () => {
			window.removeEventListener('resize', resize);
			window.cancelAnimationFrame(animationFrameRef.current);
		};
	}, []);

	return (
		<canvas
			ref={canvasRef}
			aria-hidden="true"
			style={{
				position: 'absolute',
				inset: 0,
				display: 'block',
				width: '100%',
				height: '100%',
				pointerEvents: 'none',
				zIndex: 0,
				opacity: isTransitioning ? 0.84 : 1,
				transition: 'opacity 650ms ease'
			}}
		/>
	);
}

SpotlightBackground.propTypes = {
	themeKey: PropTypes.string
};
